import React from 'react';
import PropTypes from 'prop-types';
import css from './Column.css';
import Card from './Card';
import {isUpcomingAppointment} from "../utils/waitingRoom";

function Column(props) {

  const getSubTitle = () => {
    const length = props.items.length;
    if ( length === 0 ) {
      return props.emptyText;
    } else if ( props.subTitleMask && length === 1 ) {
      return props.subTitleMask.replace("#", length).replace("(s)", "");
    } else if ( props.subTitleMask && length > 1 ) {
      return props.subTitleMask.replace("#", length).replace("(s)", "s");
    }
    return <span>&nbsp;</span>; // Empty span that occupies space
  };

  // In the checked in column, group session cards have participants that need to be filtered on status
  const getParticipants = participants => {
    if (participants && props.title === "Checked In") {
      return participants.filter(participant => participant.waiting);
    }
    return participants;
  };

  const getStudentName = item => {
    if ( item.student ) return `${item.student.firstName} ${item.student.lastName}`;
    if ( item.groupSession ) return `Group Session (${getParticipants(item.participants).length})`;

    // Assume we will always have at least one participant for non group sessions
    return `${item.participants[0].firstName} ${item.participants[0].lastName}`;
  };

  const getStudentId = item => {
    if ( item.student ) return item.student.id;
    if ( item.groupSession ) return; // No main student id for group sessions

    // Assume we will always have at least one participant for non group sessions
    return item.participants[0].id;
  };

  const getIsProspect = item => {
    if ( item.student ) return item.student.studentType === "prospect";
    if ( item.groupSession ) return false; // Doesn't matter for group sessions

    // Assume we will always have at least one participant for non group sessions
    return item.participants[0].studentType === "prospect";
  };

  const getStudentUuid = item => {
    if ( item.student ) return item.student.uuid;
    if ( item.groupSession ) return; // No main student uuid for group sessions

    // Assume we will always have at least one participant for non group sessions
    return item.participants[0].uuid;
  };

  const getServiceName = item => {
    if ( isUpcomingAppointment(item) ) return null;
    if ( item.service ) return item.service.name;
    if ( item.serviceName ) return item.serviceName;
    return "N/A";
  };

  const getServiceProviderName = item => {
    if (item.appointment) // this item came from getWaitingRoomStudents.html
      return item.appointment.takerName;
    else if (item.serviceProviderName) { // this item came from getWaitingRoomAppointments.html
      return item.serviceProviderName;
    } else {
      return '';
    }
  };

  const getStartDate = item => {
    if ( item.startDate ) return item.startDate;
    if ( item.appointment ) return item.appointment.startDate;
    return null;
  }

  const getCourse = item => {
    if ( item.courseName ) return item.courseName;
    const participants = getParticipants(item.participants);
    const participantsCourses = participants && participants
      .map(({sectionName}) => sectionName)
      .filter(sectionName => sectionName) // Filter non-empty values
      .filter((value, i, array) => array.indexOf(value) === i) // Filter unique values
      .join(", ");
    if ( participantsCourses ) return participantsCourses;
    if ( item.appointment && item.appointment.courseName ) return item.appointment.courseName;
    return "No related course";
  }

  const isSelfServiceKiosk = item => {
    const serviceId = item.serviceId || (item.service && item.service.id);
    if (serviceId && !isNaN(serviceId) && props.services) {
      const service = props.services.find(serviceRow => serviceRow.id === parseInt(serviceId));
      return (service && service.kioskType === 'SELF-SERVICE');
    }
    return false;
  };

  return (
    <div data-role={props.columnName} className={css['container']}>
      <h1>{props.title}</h1>
      <h2>{getSubTitle()}</h2>
      <div className={css['cards-container']}>
        {props.items.map((item, i) => (
          <Card
            key={i}
            name={getStudentName(item)}
            serviceName={getServiceName(item)}
            reason={item.reason || item.appointmentTypeReason.name}
            studentId={getStudentId(item)}
            studentUuid={getStudentUuid(item)}
            isProspect={getIsProspect(item)}
            arrivalDate={item.arrivalDate}
            startDate={getStartDate(item)}
            description={item.description}
            onStartMeeting={props.onStartMeeting && !isSelfServiceKiosk(item) ? props.onStartMeeting.bind(this, item) : null}
            onRemoveFromQueue={props.onRemoveFromQueue && props.onRemoveFromQueue.bind(this, item)}
            onEditDescription={props.onEditDescription && props.onEditDescription.bind(this, item)}
            onEditAppointment={props.onEditAppointment && props.onEditAppointment.bind(this, item)}
            onEditGroupSession={props.onEditGroupSession && props.onEditGroupSession.bind(this, item)}
            onStartGroupSession={props.onStartGroupSession && props.onStartGroupSession.bind(this, item)}
            courseName={getCourse(item)}
            inProgress={props.title === "In Progress" || item.status === "PROCESSING"}
            participants={getParticipants(item.participants)}
            groupSession={item.groupSession}
            openStudentFolder={props.openStudentFolder}
            waitTime={item.waitTime}
            serviceProviderName={getServiceProviderName(item)}
          />
        ))}
      </div>
    </div>
  );
}

Column.propTypes = {
  title: PropTypes.string.isRequired,
  columnName: PropTypes.string,
  emptyText: PropTypes.string,
  items: PropTypes.array.isRequired,
  services: PropTypes.array.isRequired,
  subTitleMask: PropTypes.string,
  onRemoveFromQueue: PropTypes.func,
  onStartMeeting: PropTypes.func,
  onEditDescription: PropTypes.func,
  onEditAppointment: PropTypes.func,
  onEditGroupSession: PropTypes.func,
  onStartGroupSession: PropTypes.func,
  openStudentFolder: PropTypes.func
};

export default Column;
